import { processListingParams } from '@/api/helpers';
import { ListingResponse } from '@/api/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import {
  AttemptsReport,
  CourseCompletionReportsType,
  DepartmentPerformanceReport,
  ExportUsersReportRequest,
  GroupedByEntityFilterType,
  KeyStatsReport,
  ListDepartmentPerformanceReportResponse,
  ListPerformanceOvertimeReportResponse,
  ListPhishingSignReportsResponse,
  ListSimulationCampaignsReportResponse,
  ListUsersAwarenessReportRequest,
  ListUsersAwarenessReportResponse,
  ListUsersSummaryReportRequest,
  ListUsersSummaryReportResponse,
  ListVulnerableUsersOverTimeReportResponse,
  PerformanceOvertimeReport,
  PhishingSignReports,
  ReportRequest,
  SimulationCampaignsReport,
  userAwarenessReportsType,
  VulnerableUsersOverTimeReport,
  VulnerableUsersReport,
} from '@/client/reports/types';
import { isDemoModeActive } from '@/utils/demo-mode';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export const reportsService = (client: AxiosInstance) => {
  const COURSES_BASE_URL = '/courses';
  const REPORTS_BASE_URL = (accountId?: string) =>
    `/accounts/${accountId}/reports`;

  const getCourseCompletionReports = async (
    accountId?: string,
  ): Promise<CourseCompletionReportsType> => {
    try {
      const result = await client.get<
        CourseCompletionReportsType,
        AxiosResponse<CourseCompletionReportsType>
      >(`${REPORTS_BASE_URL(accountId)}/course-completion`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUserAwarenessReports = async (
    accountId?: string,
  ): Promise<userAwarenessReportsType> => {
    try {
      const result = await client.get<
        userAwarenessReportsType,
        AxiosResponse<userAwarenessReportsType>
      >(`${REPORTS_BASE_URL(accountId)}/user-awareness`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUsersSummaryReport = async (
    accountId: string,
    params: ListUsersSummaryReportRequest,
  ): Promise<ListUsersSummaryReportResponse> => {
    try {
      const result = await client.get<
        ListUsersSummaryReportResponse,
        AxiosResponse<ListUsersSummaryReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/users-summary`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUsersAwarenessReport = async (
    accountId: string,
    params: ListUsersAwarenessReportRequest,
  ): Promise<ListUsersAwarenessReportResponse> => {
    try {
      const result = await client.get<
        ListUsersAwarenessReportResponse,
        AxiosResponse<ListUsersAwarenessReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/users-awareness`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportUsersSummaryReport = async (
    accountId: string,
    request: ExportUsersReportRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${REPORTS_BASE_URL(accountId)}/users-summary/export`,
        { ...request },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportUsersAwarenessReport = async (
    accountId: string,
    request: ExportUsersReportRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${REPORTS_BASE_URL(accountId)}/users-awareness/export`,
        { ...request },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportExecutiveSummary = async (
    accountId: string,
    request: { exportKey: string },
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${REPORTS_BASE_URL(accountId)}/executive-summary/export`,
        { ...request },
        { withCredentials: true },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const rebuildSummaryReport = async (
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${REPORTS_BASE_URL(accountId)}/users-summary/rebuild`,
        {},
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseAttemptsReport = async (
    courseId: string,
  ): Promise<AttemptsReport[]> => {
    try {
      const result = await client.get<
        AttemptsReport[],
        AxiosResponse<AttemptsReport[]>
      >(`${COURSES_BASE_URL}/${courseId}/reports/attempt-stats`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getPerformanceOvertimeReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<PerformanceOvertimeReport[]> => {
    try {
      const result = await client.get<
        ListPerformanceOvertimeReportResponse,
        AxiosResponse<ListPerformanceOvertimeReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/performance-over-time`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getKeyStatsReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<KeyStatsReport> => {
    try {
      const result = await client.get<
        KeyStatsReport,
        AxiosResponse<KeyStatsReport>
      >(`${REPORTS_BASE_URL(accountId)}/key-phishing-statistics`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getPhishingSignReports = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<PhishingSignReports[]> => {
    try {
      const result = await client.get<
        ListPhishingSignReportsResponse,
        AxiosResponse<ListPhishingSignReportsResponse>
      >(`${REPORTS_BASE_URL(accountId)}/phishing-signs-statistics`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getDepartmentPerformanceReport = async (
    accountId: string,
    params: ReportRequest & {
      groupBy: GroupedByEntityFilterType;
    },
  ): Promise<DepartmentPerformanceReport[]> => {
    try {
      const result = await client.get<
        ListDepartmentPerformanceReportResponse,
        AxiosResponse<ListDepartmentPerformanceReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/grouped-users-by-type-performance`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSimulationCampaignsReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<SimulationCampaignsReport[]> => {
    try {
      const result = await client.get<
        ListSimulationCampaignsReportResponse,
        AxiosResponse<ListSimulationCampaignsReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/simulation-campaigns`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getVulnerableUsersReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<ListingResponse<VulnerableUsersReport>> => {
    try {
      const result = await client.get<
        ListingResponse<VulnerableUsersReport>,
        AxiosResponse<ListingResponse<VulnerableUsersReport>>
      >(`${REPORTS_BASE_URL(accountId)}/vulnerable-users`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getVulnerableUsersOverTimeReport = async (
    accountId: string,
    params: ReportRequest,
  ): Promise<VulnerableUsersOverTimeReport[]> => {
    try {
      const result = await client.get<
        ListVulnerableUsersOverTimeReportResponse,
        AxiosResponse<ListVulnerableUsersOverTimeReportResponse>
      >(`${REPORTS_BASE_URL(accountId)}/vulnerable-users-over-time`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const reportsNotifyUrl = (accountId: string) => {
    const finalAccountId = isDemoModeActive()
      ? process.env.REACT_APP_DEMO_ACCOUNT_ID || accountId
      : accountId;

    return `${client.defaults.baseURL}accounts/${finalAccountId}/reports/notify`;
  };

  return {
    getCourseCompletionReports,
    getUserAwarenessReports,
    getUsersSummaryReport,
    getUsersAwarenessReport,
    exportUsersSummaryReport,
    exportUsersAwarenessReport,
    exportExecutiveSummary,
    rebuildSummaryReport,
    getCourseAttemptsReport,
    getPerformanceOvertimeReport,
    getKeyStatsReport,
    getPhishingSignReports,
    getDepartmentPerformanceReport,
    getSimulationCampaignsReport,
    getVulnerableUsersReport,
    getVulnerableUsersOverTimeReport,
    reportsNotifyUrl,
  };
};
